import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BarChart3,
  ChevronDown,
  CircleAlert,
  ExternalLink,
  Flag,
  Gauge,
  ListFilter,
  RefreshCw,
  Shield,
  ShieldCheck,
  Sparkles,
  Trash2,
  UsersRound,
} from 'lucide-react';
import { DataGrid } from '@/components/data-grid';
import type { ColumnFilterGroup } from '@/components/data-grid';
import { ROUTES } from '@/app/routes';
import { cn } from '@/lib/utils';
import { safeMediaSrc } from '@/lib/mediaUrl';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useCoopModerationMetrics, useCoopModerationCases, useCoopModerationActions } from '../api';
import type { ProactiveCase, ModerationActionEntry } from '../api';
import { proactiveColumns, actionLogColumns } from '../columns';
import { ModerationToolLinks } from '../components/ModerationToolLinks';
import { formatDateTime } from '../lib/display';

type ModerationView = 'overview' | 'queue' | 'actions' | 'history';

type Accent = 'purple' | 'blue' | 'red' | 'amber' | 'green' | 'pink';

const QUEUE_STATUS_OPTIONS = [
  { key: 'pending', label: 'Automated processing' },
  { key: 'deep', label: 'Frontier analysis' },
  { key: 'escalated', label: 'Human / specialist review' },
  { key: 'error', label: 'Pipeline error' },
];

const ACTION_TYPE_OPTIONS = [
  { key: 'approved', label: 'Approved / bypassed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'takedown', label: 'Taken down' },
  { key: 'warn', label: 'Warning' },
];

const HISTORY_FILTER_OPTIONS = [
  { key: 'approved', label: 'Approved / bypassed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'error', label: 'Pipeline error' },
];

const accentStyles: Record<Accent, { icon: string; glow: string; value: string }> = {
  purple: { icon: 'text-violet-400', glow: 'bg-violet-500/10', value: 'text-violet-100' },
  blue: { icon: 'text-sky-400', glow: 'bg-sky-500/10', value: 'text-sky-100' },
  red: { icon: 'text-rose-400', glow: 'bg-rose-500/10', value: 'text-rose-100' },
  amber: { icon: 'text-amber-400', glow: 'bg-amber-500/10', value: 'text-amber-100' },
  green: { icon: 'text-lime-400', glow: 'bg-lime-500/10', value: 'text-lime-100' },
  pink: { icon: 'text-fuchsia-400', glow: 'bg-fuchsia-500/10', value: 'text-fuchsia-100' },
};

const panelClass =
  'rounded-xl border border-white/[0.09] bg-[#111114]/95 shadow-[0_12px_36px_rgba(0,0,0,0.2)]';

export function ModerationListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<ModerationView>(() => {
    const view = searchParams.get('view');
    if (view === 'queue' || view === 'actions' || view === 'history') return view;
    return 'overview';
  });

  const queueTable = useCoopModerationCases();
  const actionsTable = useCoopModerationActions();
  const historyTable = useCoopModerationCases('completed');
  const metrics = useCoopModerationMetrics();

  const queueFilterGroups = useMemo<ColumnFilterGroup[]>(
    () => [
      { columnKey: 'status', label: 'Status', options: QUEUE_STATUS_OPTIONS, multiSelect: false },
    ],
    [],
  );
  const actionFilterGroups = useMemo<ColumnFilterGroup[]>(
    () => [
      {
        columnKey: 'actionType',
        label: 'Action',
        options: ACTION_TYPE_OPTIONS,
        multiSelect: false,
      },
    ],
    [],
  );
  const historyFilterGroups = useMemo<ColumnFilterGroup[]>(
    () => [
      {
        columnKey: 'status',
        label: 'Outcome',
        options: HISTORY_FILTER_OPTIONS,
        multiSelect: false,
      },
    ],
    [],
  );

  const queueRows = queueTable.rows.slice(0, 5);
  const metricData = metrics.data;
  // Prefer server-side /coop/metrics totals — never derive KPI depths from a single page of rows.
  const activeCaseCount = metricData?.activeCases ?? queueTable.total;
  const humanReviewCount = metricData?.inHumanQueue ?? 0;
  const frontierQueueCount =
    metricData?.frontierQueue ?? metricData?.gpuEscalationsToday ?? 0;
  const errorQueueCount = metricData?.errorQueue ?? 0;
  const pipelineProcessingCount =
    metricData?.processingQueue ??
    Math.max(activeCaseCount - humanReviewCount - errorQueueCount - frontierQueueCount, 0);
  const approvedToday = metricData?.autoApprovedToday ?? 0;
  const rejectedToday = metricData?.autoRejectedToday ?? 0;
  const resolvedToday = approvedToday + rejectedToday;
  const coverage = Math.round((resolvedToday / Math.max(resolvedToday + activeCaseCount, 1)) * 100);

  const offenderRows = useMemo(() => {
    const grouped = new Map<string, { name: string; count: number; highestRisk: number }>();
    queueTable.rows.forEach((row) => {
      const name = row.creator_name || 'Unknown creator';
      const current = grouped.get(name) ?? { name, count: 0, highestRisk: 0 };
      current.count += 1;
      current.highestRisk = Math.max(current.highestRisk, row.combined_risk ?? 0);
      grouped.set(name, current);
    });
    return [...grouped.values()]
      .sort((a, b) => b.count - a.count || b.highestRisk - a.highestRisk)
      .slice(0, 5);
  }, [queueTable.rows]);

  const categoryRows = useMemo(() => {
    const grouped = new Map<string, number>();
    queueTable.rows.forEach((row) => {
      const category = row.rule_matched || 'Unclassified';
      grouped.set(category, (grouped.get(category) ?? 0) + 1);
    });
    const rows = [...grouped.entries()]
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
    return rows.length > 0 ? rows : [{ label: 'No active rule matches', value: 0 }];
  }, [queueTable.rows]);

  const pulseValues = queueRows.map((row) => Math.round((row.combined_risk ?? 0) * 100));
  const decisionCounts = [
    { label: 'Approved / bypassed', value: approvedToday, color: '#84cc16' },
    { label: 'Rejected', value: rejectedToday, color: '#f43f5e' },
    { label: 'Human review', value: humanReviewCount, color: '#f59e0b' },
  ];

  const statusCounts = [
    { label: 'Human / specialist review', value: humanReviewCount, color: '#f43f5e' },
    { label: 'Frontier analysis', value: frontierQueueCount, color: '#a855f7' },
    {
      label: 'Automated processing',
      value: pipelineProcessingCount,
      color: '#3b82f6',
    },
    { label: 'Pipeline errors', value: errorQueueCount, color: '#fb7185' },
  ];
  const statusTotal = statusCounts.reduce((sum, item) => sum + item.value, 0);

  const openView = (view: Exclude<ModerationView, 'overview'>) => setActiveView(view);

  return (
    <div
      className={cn(
        'bg-[#0b0b0d] px-4 py-4 text-zinc-100 sm:px-6 lg:px-8',
        // A full list bounds its own height so the grid scrolls internally and its header can
        // stick; the overview is a tall dashboard that should grow and scroll with the page.
        activeView !== 'overview' ? 'flex h-full flex-col' : 'min-h-full',
      )}
    >
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 rounded-lg border border-violet-400/30 bg-violet-500/10 p-2.5 text-violet-400">
            <Shield className="size-5" aria-hidden />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[1.45rem] font-semibold tracking-tight text-white">
                Moderation System
              </h1>
              <span className="rounded-full border border-lime-400/25 bg-lime-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-lime-300">
                Live
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Monitor the moderation pipeline from automated scoring through frontier analysis,
              human review, and final decision.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ModerationToolLinks compact />
          <Button
            type="button"
            variant="outline"
            className="h-9 border-white/10 bg-[#111114] px-3 text-xs text-zinc-300 shadow-none hover:bg-white/[0.08] hover:text-white"
            onClick={() => {
              void Promise.all([queueTable.refetch(), actionsTable.refetch(), metrics.refetch()]);
              toast.success('Moderation data refreshed');
            }}
          >
            <RefreshCw className="mr-2 size-3.5" aria-hidden />
            Refresh
          </Button>
          <Button
            type="button"
            className="h-9 border-0 bg-violet-600 px-3 text-xs text-white shadow-[0_0_18px_rgba(124,58,237,0.25)] hover:bg-violet-500"
            onClick={() => toast.message('Export is planned for a later phase.')}
          >
            <ExternalLink className="mr-2 size-3.5" aria-hidden />
            Export
          </Button>
        </div>
      </header>

      {activeView !== 'overview' ? (
        <FullListView
          activeView={activeView}
          queueTable={queueTable}
          actionsTable={actionsTable}
          historyTable={historyTable}
          queueFilterGroups={queueFilterGroups}
          actionFilterGroups={actionFilterGroups}
          historyFilterGroups={historyFilterGroups}
          navigate={navigate}
          onBack={() => setActiveView('overview')}
        />
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <MetricCard
              label="Active cases"
              value={activeCaseCount}
              detail="Undecided ledger rows (live)"
              icon={<Flag />}
              accent="purple"
            />
            <MetricCard
              label="Pipeline processing"
              value={pipelineProcessingCount}
              detail="PENDING without risk tier"
              icon={<Activity />}
              accent="blue"
            />
            <MetricCard
              label="Human review"
              value={humanReviewCount}
              detail="Waiting for specialist decision"
              icon={<UsersRound />}
              accent="pink"
            />
            <MetricCard
              label="Frontier queue"
              value={frontierQueueCount}
              detail="PENDING with risk tier (live)"
              icon={<Sparkles />}
              accent="amber"
            />
            <MetricCard
              label="Approved / bypassed"
              value={approvedToday}
              detail="Final decisions today"
              icon={<ShieldCheck />}
              accent="green"
            />
            <MetricCard
              label="Rejected"
              value={rejectedToday}
              detail="Final decisions today"
              icon={<Trash2 />}
              accent="red"
            />
          </div>

          <div className="mb-4 grid items-stretch gap-4 xl:grid-cols-[minmax(0,1.75fr)_minmax(320px,1fr)]">
            <section className={cn(panelClass, 'p-4 sm:p-5')}>
              <SectionHeader
                title="Moderation overview"
                icon={<BarChart3 className="size-4" />}
                detail="Risk pulse across active cases"
                action="By queue item"
              />
              <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_190px]">
                <div className="min-w-0">
                  <div className="mb-2 flex items-center gap-4 text-[11px] text-muted-foreground">
                    <LegendDot color="#8b5cf6" label="Risk score" />
                    <LegendDot color="#38bdf8" label="Queue sample" />
                    <span className="ml-auto">Latest queue page</span>
                  </div>
                  {pulseValues.length > 0 ? (
                    <>
                      <RiskPulseChart values={pulseValues} />
                      <div className="mt-1 flex justify-between pl-8 text-[10px] text-muted-foreground">
                        {['Oldest', 'Queue', 'Latest'].map((label) => (
                          <span key={label}>{label}</span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="flex h-[190px] items-center justify-center rounded-lg border border-dashed border-white/10 text-xs text-muted-foreground">
                      No active cases to chart
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 lg:grid-cols-1">
                  <StatPill label="Active cases" value={activeCaseCount} color="text-violet-300" />
                  <StatPill label="Human review" value={humanReviewCount} color="text-rose-300" />
                  <StatPill label="Coverage" value={`${coverage}%`} color="text-lime-300" />
                </div>
              </div>
            </section>

            <section className={cn(panelClass, 'p-4 sm:p-5')}>
              <SectionHeader
                title="Queue status"
                icon={<Activity className="size-4" />}
                detail="Active stage distribution"
              />
              <div className="mt-4 flex items-center gap-5">
                <Donut value={statusTotal} segments={statusCounts} centerLabel="Active" />
                <div className="min-w-0 flex-1 space-y-2.5">
                  {statusCounts.map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
                        <span
                          className="size-2 shrink-0 rounded-sm"
                          style={{ backgroundColor: item.color }}
                        />
                        {item.label}
                      </span>
                      <span className="font-medium text-zinc-200">
                        {item.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(300px,1fr)]">
            <section className={cn(panelClass, 'p-4 sm:p-5')}>
              <SectionHeader
                title="Violation categories"
                icon={<CircleAlert className="size-4" />}
                detail="Matched in current queue"
                action="By volume"
              />
              <div className="mt-5 space-y-3">
                {categoryRows.map((item, index) => {
                  const max = Math.max(...categoryRows.map((entry) => entry.value), 1);
                  return (
                    <div
                      key={item.label}
                      className="grid grid-cols-[minmax(125px,1fr)_minmax(100px,2.5fr)_32px] items-center gap-3 text-xs"
                    >
                      <span className="truncate text-muted-foreground">{item.label}</span>
                      <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
                        <div
                          className="h-full rounded-full bg-violet-500"
                          style={{
                            width: `${Math.max((item.value / max) * 100, item.value ? 8 : 0)}%`,
                            opacity: 1 - index * 0.1,
                          }}
                        />
                      </div>
                      <span className="text-right text-zinc-300">{item.value}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className={cn(panelClass, 'p-4 sm:p-5')}>
              <SectionHeader
                title="Workflow efficiency"
                icon={<Gauge className="size-4" />}
                detail="Decision coverage"
              />
              <div className="mt-4 flex items-center gap-5">
                <Donut
                  value={coverage}
                  segments={[
                    { label: 'Covered', value: coverage, color: '#8b5cf6' },
                    { label: 'Open', value: 100 - coverage, color: '#292532' },
                  ]}
                  centerLabel="Coverage"
                  percentage
                />
                <div className="flex-1 space-y-3 text-xs">
                  <EfficiencyRow label="Active cases" value={activeCaseCount.toLocaleString()} />
                  <EfficiencyRow label="Human review" value={humanReviewCount.toLocaleString()} />
                  <EfficiencyRow
                    label="Frontier queue"
                    value={frontierQueueCount.toLocaleString()}
                  />
                  <EfficiencyRow
                    label="Final decision rate"
                    value={`${Math.round((resolvedToday / Math.max(activeCaseCount + resolvedToday, 1)) * 100)}%`}
                  />
                </div>
              </div>
            </section>
          </div>

          <section className={cn(panelClass, 'mb-4 overflow-hidden')}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[0.08] px-4 py-3 sm:px-5">
              <div>
                <h2 className="text-sm font-semibold text-white">Moderation workflow</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Pending → frontier → human review → final decision
                </p>
              </div>
              <button
                type="button"
                onClick={() => openView('queue')}
                className="flex items-center gap-1 text-xs text-violet-300 transition-colors hover:text-violet-200"
              >
                Open queue <ArrowRight className="size-3.5" />
              </button>
            </div>
            <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <WorkflowStage
                number="1"
                label="Open + decided today"
                value={activeCaseCount + resolvedToday}
                description="Live open plus decisions today"
                icon={<Flag />}
                accent="purple"
              />
              <WorkflowStage
                number="2"
                label="Automated processing"
                value={pipelineProcessingCount}
                description="PENDING, no risk tier"
                icon={<Activity />}
                accent="blue"
              />
              <WorkflowStage
                number="3"
                label="Frontier analysis"
                value={frontierQueueCount}
                description="PENDING with risk tier"
                icon={<Sparkles />}
                accent="amber"
              />
              <WorkflowStage
                number="4"
                label="Human review"
                value={humanReviewCount}
                description="Specialist decision queue"
                icon={<UsersRound />}
                accent="pink"
              />
              <WorkflowStage
                number="5"
                label="Approved / bypassed"
                value={approvedToday}
                description="Final pass-through today"
                icon={<ShieldCheck />}
                accent="green"
              />
              <WorkflowStage
                number="6"
                label="Rejected"
                value={rejectedToday}
                description="Final blocked today"
                icon={<Trash2 />}
                accent="red"
                last
              />
            </div>
          </section>

          <div className="grid gap-4 lg:grid-cols-3">
            <RecentFlaggedCard
              rows={queueRows}
              onViewAll={() => openView('queue')}
              onRowClick={(row) => void navigate(ROUTES.contentDetails(String(row.post_id)))}
            />
            <TopOffendersCard rows={offenderRows} onViewAll={() => openView('queue')} />
            <DecisionMixCard
              counts={decisionCounts}
              total={decisionCounts.reduce((sum, item) => sum + item.value, 0)}
              onViewAll={() => openView('actions')}
            />
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-violet-400/15 bg-violet-500/[0.06] px-4 py-3 text-xs text-muted-foreground">
            <InfoIcon />
            <span>
              Ledger states are PENDING (processing or frontier via risk tier), HUMAN_REVIEW, ERROR,
              then APPROVED / BYPASSED / REJECTED. Queue depths are live snapshots; approved and
              rejected counts are for today.
            </span>
            <button
              type="button"
              onClick={() => openView('queue')}
              className="ml-auto shrink-0 text-violet-300 hover:text-violet-200"
            >
              View queue <ArrowRight className="ml-1 inline size-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function FullListView({
  activeView,
  queueTable,
  actionsTable,
  historyTable,
  queueFilterGroups,
  actionFilterGroups,
  historyFilterGroups,
  navigate,
  onBack,
}: {
  activeView: Exclude<ModerationView, 'overview'>;
  queueTable: ReturnType<typeof useCoopModerationCases>;
  actionsTable: ReturnType<typeof useCoopModerationActions>;
  historyTable: ReturnType<typeof useCoopModerationCases>;
  queueFilterGroups: ColumnFilterGroup[];
  actionFilterGroups: ColumnFilterGroup[];
  historyFilterGroups: ColumnFilterGroup[];
  navigate: ReturnType<typeof useNavigate>;
  onBack: () => void;
}) {
  const isQueue = activeView === 'queue';
  const isActions = activeView === 'actions';
  const title = isQueue
    ? 'Moderation Queue'
    : isActions
      ? 'Moderation Actions'
      : 'Decision History';
  const rowClick = (row: ProactiveCase | ModerationActionEntry) => {
    const targetId = 'post_id' in row ? row.post_id : row.target_id;
    if (targetId != null) void navigate(ROUTES.contentDetails(String(targetId)));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200"
          >
            <ArrowRight className="size-3 rotate-180" /> Back to overview
          </button>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review active pipeline stages, human escalations, and terminal case decisions.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#111114] px-3 py-2 text-xs text-muted-foreground">
          <ListFilter className="size-3.5" /> Filters are server-backed
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {isQueue ? (
          <DataGrid<ProactiveCase>
            title={title}
            searchPlaceholder="Search items in queue…"
            columns={proactiveColumns}
            rows={queueTable.rows}
            total={queueTable.total}
            isLoading={queueTable.isLoading}
            isFetchingNextPage={queueTable.isFetchingNextPage}
            isError={queueTable.isError}
            error={queueTable.error}
            endReached={queueTable.endReached}
            page={queueTable.page}
            totalPage={queueTable.totalPage}
            onPageChange={queueTable.setPage}
            isFetching={queueTable.isFetching}
            onLoadMore={queueTable.fetchNextPage}
            onRetry={() => void queueTable.refetch()}
            search={queueTable.search}
            onSearchChange={queueTable.setSearch}
            filterGroups={queueFilterGroups}
            selectedFilters={queueTable.filters}
            onFiltersChange={queueTable.setFilters}
            onRowClick={rowClick}
            getRowId={(row) => row.case_id}
            emptyMessage="Queue is empty — the pipeline is handling everything automatically."
          />
        ) : isActions ? (
          <DataGrid<ModerationActionEntry>
            title={title}
            searchPlaceholder="Search actions…"
            columns={actionLogColumns}
            rows={actionsTable.rows}
            total={actionsTable.total}
            isLoading={actionsTable.isLoading}
            isFetchingNextPage={actionsTable.isFetchingNextPage}
            isError={actionsTable.isError}
            error={actionsTable.error}
            endReached={actionsTable.endReached}
            page={actionsTable.page}
            totalPage={actionsTable.totalPage}
            onPageChange={actionsTable.setPage}
            isFetching={actionsTable.isFetching}
            onLoadMore={actionsTable.fetchNextPage}
            onRetry={() => void actionsTable.refetch()}
            search={actionsTable.search}
            onSearchChange={actionsTable.setSearch}
            filterGroups={actionFilterGroups}
            selectedFilters={actionsTable.filters}
            onFiltersChange={actionsTable.setFilters}
            onRowClick={rowClick}
            getRowId={(row) => row.id}
            emptyMessage="No moderation actions recorded yet."
          />
        ) : (
          <DataGrid<ProactiveCase>
            title={title}
            searchPlaceholder="Search past decisions…"
            columns={proactiveColumns}
            rows={historyTable.rows}
            total={historyTable.total}
            isLoading={historyTable.isLoading}
            isFetchingNextPage={historyTable.isFetchingNextPage}
            isError={historyTable.isError}
            error={historyTable.error}
            endReached={historyTable.endReached}
            page={historyTable.page}
            totalPage={historyTable.totalPage}
            onPageChange={historyTable.setPage}
            isFetching={historyTable.isFetching}
            onLoadMore={historyTable.fetchNextPage}
            onRetry={() => void historyTable.refetch()}
            search={historyTable.search}
            onSearchChange={historyTable.setSearch}
            filterGroups={historyFilterGroups}
            selectedFilters={historyTable.filters}
            onFiltersChange={historyTable.setFilters}
            onRowClick={rowClick}
            getRowId={(row) => row.case_id}
            emptyMessage="No moderation history yet."
          />
        )}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  icon,
  accent,
}: {
  label: string;
  value: number;
  detail: string;
  icon: ReactNode;
  accent: Accent;
}) {
  const styles = accentStyles[accent];
  return (
    <div className={cn(panelClass, 'min-w-0 p-3.5')}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className={cn('rounded-md p-1.5', styles.glow, styles.icon)}>{icon}</span>
        <span className="truncate">{label}</span>
      </div>
      <div className={cn('mt-2 text-2xl font-semibold tracking-tight', styles.value)}>
        {value.toLocaleString()}
      </div>
      <p className="mt-1 truncate text-[10px] text-muted-foreground">{detail}</p>
    </div>
  );
}

function SectionHeader({
  title,
  detail,
  icon,
  action,
}: {
  title: string;
  detail: string;
  icon: ReactNode;
  action?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="text-violet-400">{icon}</span>
        <div>
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{detail}</p>
        </div>
      </div>
      {action && (
        <span className="flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] text-muted-foreground">
          {action}
          <ChevronDown className="size-3" />
        </span>
      )}
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="size-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function StatPill({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.025] p-2.5">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-lg font-semibold', color)}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

function RiskPulseChart({ values }: { values: number[] }) {
  const width = 640;
  const height = 190;
  const points = values
    .map((value, index) => {
      const x = 14 + (index / Math.max(values.length - 1, 1)) * (width - 30);
      const y = height - 20 - (Math.min(value, 100) / 100) * (height - 42);
      return `${x},${y}`;
    })
    .join(' ');
  const area = `14,${height - 20} ${points} ${width - 16},${height - 20}`;
  return (
    <div className="relative h-[190px] overflow-hidden rounded-lg border border-white/[0.06] bg-[#0d0d10]">
      <div className="pointer-events-none absolute inset-x-0 top-1/4 border-t border-dashed border-white/[0.06]" />
      <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-white/[0.06]" />
      <div className="pointer-events-none absolute inset-x-0 top-3/4 border-t border-dashed border-white/[0.06]" />
      <div className="absolute inset-y-0 left-0 flex w-8 flex-col justify-between py-3 text-[9px] text-muted-foreground">
        <span>100</span>
        <span>50</span>
        <span>0</span>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="h-full w-full pl-8 pr-3"
        role="img"
        aria-label="Risk score pulse across current moderation queue"
      >
        <polygon points={area} fill="url(#riskArea)" />
        <polyline
          points={points}
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {values.map((value, index) => {
          const [x, y] = points.split(' ')[index]?.split(',') ?? ['0', '0'];
          return (
            <circle
              key={`${value}-${index}`}
              cx={x}
              cy={y}
              r="4"
              fill="#111114"
              stroke="#a78bfa"
              strokeWidth="2"
            />
          );
        })}
        <defs>
          <linearGradient id="riskArea" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

function Donut({
  value,
  segments,
  centerLabel,
  percentage = false,
}: {
  value: number;
  segments: { label: string; value: number; color: string }[];
  centerLabel: string;
  percentage?: boolean;
}) {
  const total = segments.reduce((sum, item) => sum + item.value, 0) || 1;
  let cursor = 0;
  const stops = segments
    .map((item) => {
      const start = (cursor / total) * 100;
      cursor += item.value;
      return `${item.color} ${start}% ${(cursor / total) * 100}%`;
    })
    .join(', ');
  return (
    <div
      className="relative size-32 shrink-0 rounded-full p-[12px]"
      style={{ background: `conic-gradient(${stops})` }}
    >
      <div className="flex h-full w-full flex-col items-center justify-center rounded-full bg-[#111114]">
        <span className="text-xl font-semibold text-white">
          {percentage ? `${value}%` : value.toLocaleString()}
        </span>
        <span className="text-[9px] text-muted-foreground">{centerLabel}</span>
      </div>
    </div>
  );
}

function EfficiencyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-zinc-200">{value}</span>
    </div>
  );
}

function WorkflowStage({
  number,
  label,
  value,
  description,
  icon,
  accent,
  last = false,
}: {
  number: string;
  label: string;
  value: number;
  description: string;
  icon: ReactNode;
  accent: Accent;
  last?: boolean;
}) {
  const styles = accentStyles[accent];
  return (
    <div className="relative flex min-w-0 items-center gap-2 rounded-lg border border-white/[0.09] bg-white/[0.02] p-3">
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-full border border-current/30',
          styles.glow,
          styles.icon,
        )}
      >
        {icon}
      </span>
      <div className="min-w-0">
        <p className="truncate text-[10px] text-muted-foreground">
          <span className="mr-1 text-muted-foreground">{number}.</span>
          {label}
        </p>
        <p className="mt-0.5 text-lg font-semibold text-white">{value.toLocaleString()}</p>
        <p className="truncate text-[10px] text-muted-foreground">{description}</p>
      </div>
      {!last && (
        <ArrowRight className="absolute -right-3 z-10 hidden size-3.5 text-muted-foreground xl:block" />
      )}
    </div>
  );
}

function RecentFlaggedCard({
  rows,
  onViewAll,
  onRowClick,
}: {
  rows: ProactiveCase[];
  onViewAll: () => void;
  onRowClick: (row: ProactiveCase) => void;
}) {
  return (
    <section className={cn(panelClass, 'overflow-hidden')}>
      <CardHeader
        title="Recent active cases"
        icon={<Flag className="size-4" />}
        onViewAll={onViewAll}
      />
      <div className="divide-y divide-white/[0.06]">
        {rows.length === 0 ? (
          <EmptyRows label="No flagged content in the current queue." />
        ) : (
          rows.map((row) => (
            <button
              key={row.case_id}
              type="button"
              onClick={() => onRowClick(row)}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-white/[0.04]"
            >
              <Thumb src={row.thumbnail_url} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-zinc-300">
                  {row.caption_preview || row.content_description || 'Untitled content'}
                </p>
                <p className="mt-0.5 truncate text-[10px] text-muted-foreground">
                  {row.creator_name || 'Unknown creator'} · {formatDateTime(row.opened_at)}
                </p>
              </div>
              <StatusBadge status={row.status} />
            </button>
          ))
        )}
      </div>
      <CardFooter label="View all active cases" onClick={onViewAll} />
    </section>
  );
}

function TopOffendersCard({
  rows,
  onViewAll,
}: {
  rows: { name: string; count: number; highestRisk: number }[];
  onViewAll: () => void;
}) {
  return (
    <section className={cn(panelClass, 'overflow-hidden')}>
      <CardHeader
        title="Creators in active review"
        icon={<UsersRound className="size-4" />}
        onViewAll={onViewAll}
      />
      <div className="divide-y divide-white/[0.06]">
        {rows.length === 0 ? (
          <EmptyRows label="Creator patterns will appear as the queue fills." />
        ) : (
          rows.map((row, index) => (
            <div key={row.name} className="flex items-center gap-3 px-4 py-2.5">
              <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-[10px] font-semibold text-violet-300">
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-zinc-300">{row.name}</p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Highest risk {Math.round(row.highestRisk * 100)}%
                </p>
              </div>
              <span className="text-xs font-medium text-zinc-300">
                {row.count} <span className="text-[10px] font-normal text-muted-foreground">flags</span>
              </span>
            </div>
          ))
        )}
      </div>
      <CardFooter label="Review creators in queue" onClick={onViewAll} />
    </section>
  );
}

function DecisionMixCard({
  counts,
  total,
  onViewAll,
}: {
  counts: { label: string; value: number; color: string }[];
  total: number;
  onViewAll: () => void;
}) {
  return (
    <section className={cn(panelClass, 'overflow-hidden')}>
      <CardHeader
        title="Decision mix"
        icon={<ShieldCheck className="size-4" />}
        onViewAll={onViewAll}
      />
      <div className="flex items-center gap-4 px-4 py-4">
        <Donut value={total} segments={counts} centerLabel="Decisions" />
        <div className="min-w-0 flex-1 space-y-3">
          {counts.map((item) => (
            <div key={item.label} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-2 text-muted-foreground">
                <span className="size-2 rounded-sm" style={{ backgroundColor: item.color }} />
                {item.label}
              </span>
              <span className="font-medium text-zinc-200">{item.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
      <CardFooter label="View action history" onClick={onViewAll} />
    </section>
  );
}

function CardHeader({
  title,
  icon,
  onViewAll,
}: {
  title: string;
  icon: ReactNode;
  onViewAll: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-violet-400">{icon}</span>
        <h2 className="text-sm font-semibold text-white">{title}</h2>
      </div>
      <button
        type="button"
        onClick={onViewAll}
        className="rounded-md border border-white/10 px-2 py-1 text-[10px] text-muted-foreground hover:bg-white/[0.05] hover:text-white"
      >
        View all
      </button>
    </div>
  );
}

function CardFooter({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-1 border-t border-white/[0.08] px-4 py-2.5 text-left text-[11px] text-violet-300 transition-colors hover:bg-violet-500/[0.05]"
    >
      {label}
      <ArrowRight className="size-3.5" />
    </button>
  );
}

function Thumb({ src }: { src: string | null }) {
  const safeSrc = safeMediaSrc(src);
  return safeSrc ? (
    <img src={safeSrc} alt="" className="size-9 shrink-0 rounded object-cover" loading="lazy" />
  ) : (
    <div className="flex size-9 shrink-0 items-center justify-center rounded bg-white/[0.06] text-muted-foreground">
      <Flag className="size-3.5" />
    </div>
  );
}

function StatusBadge({ status }: { status: ProactiveCase['status'] }) {
  const config: Record<ProactiveCase['status'], { label: string; className: string }> = {
    pending: {
      label: 'Awaiting pipeline',
      className: 'border-sky-400/30 bg-sky-400/10 text-sky-300',
    },
    heuristic: {
      label: 'Heuristic scoring',
      className: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-300',
    },
    standard: {
      label: 'Standard GPU analysis',
      className: 'border-amber-400/30 bg-amber-400/10 text-amber-300',
    },
    deep: {
      label: 'Frontier model analysis',
      className: 'border-purple-400/30 bg-purple-400/10 text-purple-300',
    },
    escalated: {
      label: 'Human / specialist review',
      className: 'border-fuchsia-400/30 bg-fuchsia-400/10 text-fuchsia-300',
    },
    approved: {
      label: 'Approved / bypassed',
      className: 'border-lime-400/30 bg-lime-400/10 text-lime-300',
    },
    rejected: { label: 'Rejected', className: 'border-rose-400/30 bg-rose-400/10 text-rose-300' },
    error: {
      label: 'Pipeline error',
      className: 'border-rose-400/30 bg-rose-400/10 text-rose-300',
    },
  };
  const item = config[status] ?? config.pending;
  return (
    <span className={cn('shrink-0 rounded-full border px-2 py-1 text-[10px]', item.className)}>
      {item.label}
    </span>
  );
}

function EmptyRows({ label }: { label: string }) {
  return <div className="px-4 py-8 text-center text-xs text-muted-foreground">{label}</div>;
}

function InfoIcon() {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-sky-400/40 text-sky-300">
      <span className="text-[11px] font-semibold">i</span>
    </span>
  );
}
